import React, { useCallback, useMemo, type ReactElement, type Ref } from 'react';
import { useGetCurrentUrl } from '@jira/navigation-apps__routing-hooks/src/useGetCurrentUrl.tsx';
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import { PremiumLozenge } from '@jira/polaris__component-premium-lozenge/src/PremiumLozenge.tsx';
import { SECTION_ITEM_ROADMAPS } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/section-type.tsx';
import { MENU_ID_ROADMAPS } from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import {
	L1_MENU_ID,
	type Nav4DragAndDropMenuItemProps,
} from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { Nav4ExpandableMenuItem } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/nav4-expandable-menu-item/index.tsx';
import { SkeletonNavMenuItems } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/skeleton-nav-menu-items/main.tsx';
import { getTestId } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/get-test-id/index.tsx';
import { useSectionItemAnalytics } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/use-section-item-analytics/index.tsx';
import { useConfigurableMessageAndIconMap } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/controllers/use-configurable-message-and-icon-map/index.tsx';
import { useCurrentUser } from '@jira/platform__services-user-current/src/main.tsx';
// eslint-disable-next-line jira/import/file-extension-required
import { componentWithFG } from '@jira/platform__utils-feature-gates-gate-component';
import {
	ExpandableMenuItemContent,
	ExpandableMenuItemTrigger,
} from '@atlaskit/side-nav-items/expandable-menu-item';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useActivationId } from '@atlassian/jira-tenant-context-controller/src/components/activation-id/index.tsx';
import { Trigger } from '@atlassian/entry-point-panel/trigger';
import { usePanel } from '@atlassian/entry-point-panel/use-panel';
import { mergeRefs } from '@atlassian/jira-merge-refs';

import { Footer } from './common/ui/footer/index.tsx';
import { nav4RoadmapsContentEntryPoint } from './nav4RoadmapsContentEntryPoint.entrypoint.tsx';

type Nav4RoadmapsDefaultProps = Nav4DragAndDropMenuItemProps & {
	actionsOnHover?: ReactElement;
	isExpanded: boolean;
	simulateLoading?: boolean; // For testing only
};

function Nav4RoadmapsDefaultGSN(props: Nav4RoadmapsDefaultProps): React.JSX.Element {
	const {
		actionsOnHover,
		draggableRef,
		dropIndicator,
		hasDragIndicator,
		isDragging,
		isExpanded,
		onExpandFlyoutToggle,
		simulateLoading = false,
		visualContentRef,
	} = props;
	const cloudId = useCloudId();
	const currentURL = useGetCurrentUrl();
	const {
		data: {
			user: { accountId },
		},
	} = useCurrentUser();
	const activationId = useActivationId();

	// Ignore this error as our component does not need the context.
	// @ts-expect-error - Property 'context' is missing in type '{ tenantContext: { cloudId: string; atlassianAccountId: string | null; activationId: string; }; currentURL: string; }' but required in type 'EntryPointRouteParams'.ts(2741)
	const entryPointParams: EntryPointRouteParams = useMemo(
		() => ({ tenantContext: { cloudId, atlassianAccountId: accountId, activationId }, currentURL }),
		[cloudId, accountId, activationId, currentURL],
	);

	const { Panel, trigger } = usePanel();

	const { formattedMessage, icon } = useConfigurableMessageAndIconMap(L1_MENU_ID.ROADMAPS);
	const sendSectionItemAnalyticsEvent = useSectionItemAnalytics();

	const menuId = MENU_ID_ROADMAPS;

	const handleExpansionToggle = useCallback(
		(hasExpanded: boolean) => {
			if (hasExpanded) {
				sendSectionItemAnalyticsEvent({
					menuId: L1_MENU_ID.ROADMAPS,
					sectionItem: SECTION_ITEM_ROADMAPS,
				});
			}
			onExpandFlyoutToggle?.(hasExpanded);
		},
		[onExpandFlyoutToggle, sendSectionItemAnalyticsEvent],
	);

	return (
		<Nav4ExpandableMenuItem
			dropTargetRef={visualContentRef}
			dropIndicator={dropIndicator}
			isDragging={isDragging}
			isExpanded={isExpanded}
			menuId={menuId}
			onExpansionToggle={handleExpansionToggle}
		>
			<Trigger
				trigger={trigger}
				entryPoint={nav4RoadmapsContentEntryPoint}
				entryPointParams={entryPointParams}
				fallback={<SkeletonNavMenuItems />}
			>
				{({ ref }) => (
					<>
						<ExpandableMenuItemTrigger
							interactionName="nav4-sidebar-roadmaps-default"
							actionsOnHover={actionsOnHover}
							aria-label={formattedMessage}
							elemBefore={icon}
							hasDragIndicator={hasDragIndicator}
							isDragging={isDragging}
							elemAfter={<PremiumLozenge />}
							ref={
								draggableRef
									? // oxlint-disable-next-line typescript/consistent-type-assertions
										mergeRefs(ref as Ref<HTMLButtonElement>, draggableRef)
									: // oxlint-disable-next-line typescript/consistent-type-assertions
										(ref as Ref<HTMLButtonElement>)
							}
							testId={getTestId(menuId)}
						>
							{formattedMessage}
						</ExpandableMenuItemTrigger>
						<ExpandableMenuItemContent>
							{simulateLoading ? <SkeletonNavMenuItems /> : <Panel />}
							<Footer />
						</ExpandableMenuItemContent>
					</>
				)}
			</Trigger>
		</Nav4ExpandableMenuItem>
	);
}

function Nav4RoadmapsDefaultNav4(props: Nav4RoadmapsDefaultProps): React.JSX.Element {
	const { actionsOnHover, isExpanded, simulateLoading = false } = props;
	const cloudId = useCloudId();
	const currentURL = useGetCurrentUrl();
	const {
		data: {
			user: { accountId },
		},
	} = useCurrentUser();
	const activationId = useActivationId();

	// Ignore this error as our component does not need the context.
	// @ts-expect-error - Property 'context' is missing in type '{ tenantContext: { cloudId: string; atlassianAccountId: string | null; activationId: string; }; currentURL: string; }' but required in type 'EntryPointRouteParams'.ts(2741)
	const entryPointParams: EntryPointRouteParams = useMemo(
		() => ({ tenantContext: { cloudId, atlassianAccountId: accountId, activationId }, currentURL }),
		[cloudId, accountId, activationId, currentURL],
	);

	const { Panel, trigger } = usePanel();

	const { formattedMessage, icon } = useConfigurableMessageAndIconMap(L1_MENU_ID.ROADMAPS);
	const sendSectionItemAnalyticsEvent = useSectionItemAnalytics();

	const menuId = MENU_ID_ROADMAPS;

	const handleExpansionToggle = useCallback(
		(hasExpanded: boolean) => {
			if (hasExpanded) {
				sendSectionItemAnalyticsEvent({
					menuId: L1_MENU_ID.ROADMAPS,
					sectionItem: SECTION_ITEM_ROADMAPS,
				});
			}
		},
		[sendSectionItemAnalyticsEvent],
	);

	return (
		<Nav4ExpandableMenuItem
			isExpanded={isExpanded}
			menuId={menuId}
			onExpansionToggle={handleExpansionToggle}
		>
			<Trigger
				trigger={trigger}
				entryPoint={nav4RoadmapsContentEntryPoint}
				entryPointParams={entryPointParams}
				fallback={<SkeletonNavMenuItems />}
			>
				{({ ref }) => (
					<>
						<ExpandableMenuItemTrigger
							interactionName="nav4-sidebar-roadmaps-default"
							actionsOnHover={actionsOnHover}
							aria-label={formattedMessage}
							elemAfter={<PremiumLozenge />}
							elemBefore={icon}
							// oxlint-disable-next-line typescript/consistent-type-assertions
							ref={ref as Ref<HTMLButtonElement>}
							testId={getTestId(menuId)}
						>
							{formattedMessage}
						</ExpandableMenuItemTrigger>
						<ExpandableMenuItemContent>
							{simulateLoading ? <SkeletonNavMenuItems /> : <Panel />}
							<Footer />
						</ExpandableMenuItemContent>
					</>
				)}
			</Trigger>
		</Nav4ExpandableMenuItem>
	);
}

export const Nav4RoadmapsDefault: React.FC<Nav4RoadmapsDefaultProps> = componentWithFG(
	'navx-global-side-nav',
	Nav4RoadmapsDefaultGSN,
	Nav4RoadmapsDefaultNav4,
);
